import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { getRankProgress, RANK_XP_THRESHOLDS, TITLE_DEFINITIONS } from "@/lib/titles";
import { useGamification } from "@/contexts/GamificationContext";
import type { AchievementsTabId, GamificationStats } from "@/stores/useGamificationStore";
import {
  Award,
  Baby,
  Book,
  BookMarked,
  BookOpen,
  Brain,
  Check,
  Crown,
  Eye,
  Flame,
  Gamepad2,
  Gem,
  Ghost,
  GraduationCap,
  Image,
  Languages,
  Lock,
  Moon,
  PenTool,
  Rocket,
  Search,
  Sparkles,
  Sprout,
  Star,
  Store,
  Sun,
  Trophy,
  TrendingUp,
  X,
  Zap,
} from "lucide-react";
import Leaderboard from "@/pages/Leaderboard";

type TabId = "badges" | "streaks" | "titles" | "shop";

interface AchievementsDashboardProps {
  /** When true, renders as full-page; otherwise as modal overlay */
  asPage?: boolean;
  open?: boolean;
  onClose?: () => void;
  /** When provided, switch to this tab when the modal/page opens */
  initialTab?: AchievementsTabId | null;
  points?: number;
}

function playPurchaseSound() {
  const audio = new Audio("/coin.mp3");
  audio.play().catch((e) => console.error("Sound play failed", e));
}

type TierId = "bronze" | "silver" | "gold" | "platinum" | "diamond";

// Badge: thresholds = [bronze, silver, gold, platinum, diamond] milestones
// statKey maps to store.stats (or "currentStreak" for streak badge)
type StatKey = keyof GamificationStats | "currentStreak";

interface BadgeDef {
  id: string;
  icon: string;
  lucideIcon: "BookOpen" | "Flame" | "Sparkles" | "Award" | "BookMarked" | "Sun" | "Moon" | "PenTool" | "Image" | "Gem";
  thresholds: [number, number, number, number, number];
  statKey: StatKey;
}

const BADGE_LUCIDE_ICONS: Record<BadgeDef["lucideIcon"], React.ReactNode> = {
  BookOpen: <BookOpen size={26} strokeWidth={2} />,
  Flame: <Flame size={26} strokeWidth={2} />,
  Sparkles: <Sparkles size={26} strokeWidth={2} />,
  Award: <Award size={26} strokeWidth={2} />,
  BookMarked: <BookMarked size={26} strokeWidth={2} />,
  Sun: <Sun size={26} strokeWidth={2} />,
  PenTool: <PenTool size={26} strokeWidth={2} />,
  Image: <Image size={26} strokeWidth={2} />,
  Gem: <Gem size={26} strokeWidth={2} />,
  Moon: <Moon size={26} strokeWidth={2} />,
};

/** Distinct color themes for each badge icon — vibrant, vector-style */
const BADGE_ICON_STYLES: Record<string, { bg: string; icon: string }> = {
  lexiconMaster: { bg: "bg-blue-100 dark:bg-blue-500/20", icon: "text-blue-600 dark:text-blue-400" },
  streakManiac: { bg: "bg-orange-100 dark:bg-orange-500/20", icon: "text-orange-600 dark:text-orange-400" },
  aiPioneer: { bg: "bg-purple-100 dark:bg-purple-500/20", icon: "text-purple-600 dark:text-purple-400" },
  perfectStudent: { bg: "bg-yellow-100 dark:bg-yellow-500/20", icon: "text-yellow-600 dark:text-yellow-400" },
  encyclopedia: { bg: "bg-indigo-100 dark:bg-indigo-500/20", icon: "text-indigo-600 dark:text-indigo-400" },
  earlyBird: { bg: "bg-amber-100 dark:bg-amber-500/20", icon: "text-amber-600 dark:text-amber-400" },
  knowledgeArchitect: { bg: "bg-sky-100 dark:bg-sky-500/20", icon: "text-sky-600 dark:text-sky-400" },
  visualizer: { bg: "bg-pink-100 dark:bg-pink-500/20", icon: "text-pink-600 dark:text-pink-400" },
  nightOwl: { bg: "bg-violet-100 dark:bg-violet-500/20", icon: "text-violet-600 dark:text-violet-400" },
  crystalMogul: { bg: "bg-cyan-100 dark:bg-cyan-500/20", icon: "text-cyan-600 dark:text-cyan-400" },
  studyTimeMaster: { bg: "bg-emerald-100 dark:bg-emerald-500/20", icon: "text-emerald-600 dark:text-emerald-400" },
  appTimeMaster: { bg: "bg-teal-100 dark:bg-teal-500/20", icon: "text-teal-600 dark:text-teal-400" },
};

const BADGE_DEFINITIONS: BadgeDef[] = [
  { id: "lexiconMaster", icon: "📖", lucideIcon: "BookOpen", thresholds: [50, 500, 2500, 10000, 25000], statKey: "wordsLearned" },
  { id: "streakManiac", icon: "🔥", lucideIcon: "Flame", thresholds: [7, 30, 90, 180, 365], statKey: "currentStreak" },
  { id: "aiPioneer", icon: "🤖", lucideIcon: "Sparkles", thresholds: [50, 250, 1000, 5000, 10000], statKey: "aiWordsGenerated" },
  { id: "perfectStudent", icon: "💎", lucideIcon: "Award", thresholds: [5, 25, 100, 250, 500], statKey: "testsPassed" },
  { id: "encyclopedia", icon: "📚", lucideIcon: "BookMarked", thresholds: [500, 5000, 25000, 100000, 500000], statKey: "cardsViewed" },
  { id: "earlyBird", icon: "🌅", lucideIcon: "Sun", thresholds: [10, 50, 200, 500, 1000], statKey: "earlyBirdSessions" },
  { id: "knowledgeArchitect", icon: "🏗️", lucideIcon: "PenTool", thresholds: [5, 20, 50, 100, 200], statKey: "decksCreated" },
  { id: "visualizer", icon: "🖼️", lucideIcon: "Image", thresholds: [20, 100, 500, 2000, 5000], statKey: "cardsWithImages" },
  { id: "nightOwl", icon: "🦉", lucideIcon: "Moon", thresholds: [10, 50, 200, 500, 1000], statKey: "nightOwlSessions" },
  { id: "crystalMogul", icon: "💎", lucideIcon: "Gem", thresholds: [1000, 10000, 50000, 250000, 1000000], statKey: "totalCrystalsEarned" },
  { id: "studyTimeMaster", icon: "⏱️", lucideIcon: "BookOpen", thresholds: [60, 360, 1440, 7200, 43200], statKey: "totalStudyMinutes" },
  { id: "appTimeMaster", icon: "📱", lucideIcon: "Award", thresholds: [120, 720, 4320, 21600, 86400], statKey: "totalSiteMinutes" },
];

/** Color themes for title/rank icons — vibrant, vector-style */
const TITLE_ICON_STYLES: Record<string, { bg: string; icon: string }> = {
  Baby: { bg: "bg-pink-100 dark:bg-pink-500/20", icon: "text-pink-600 dark:text-pink-400" },
  Sprout: { bg: "bg-green-100 dark:bg-green-500/20", icon: "text-green-600 dark:text-green-400" },
  Search: { bg: "bg-blue-100 dark:bg-blue-500/20", icon: "text-blue-600 dark:text-blue-400" },
  GraduationCap: { bg: "bg-amber-100 dark:bg-amber-500/20", icon: "text-amber-600 dark:text-amber-400" },
  Book: { bg: "bg-indigo-100 dark:bg-indigo-500/20", icon: "text-indigo-600 dark:text-indigo-400" },
  Languages: { bg: "bg-violet-100 dark:bg-violet-500/20", icon: "text-violet-600 dark:text-violet-400" },
  Award: { bg: "bg-yellow-100 dark:bg-yellow-500/20", icon: "text-yellow-600 dark:text-yellow-400" },
  Star: { bg: "bg-amber-100 dark:bg-amber-500/20", icon: "text-amber-500 dark:text-amber-400" },
  Crown: { bg: "bg-yellow-100 dark:bg-yellow-500/20", icon: "text-yellow-600 dark:text-yellow-400" },
  Ghost: { bg: "bg-slate-100 dark:bg-slate-500/20", icon: "text-slate-600 dark:text-slate-400" },
  Zap: { bg: "bg-orange-100 dark:bg-orange-500/20", icon: "text-orange-600 dark:text-orange-400" },
  Sparkles: { bg: "bg-purple-100 dark:bg-purple-500/20", icon: "text-purple-600 dark:text-purple-400" },
  Brain: { bg: "bg-rose-100 dark:bg-rose-500/20", icon: "text-rose-600 dark:text-rose-400" },
  BookOpen: { bg: "bg-sky-100 dark:bg-sky-500/20", icon: "text-sky-600 dark:text-sky-400" },
  Eye: { bg: "bg-cyan-100 dark:bg-cyan-500/20", icon: "text-cyan-600 dark:text-cyan-400" },
  Rocket: { bg: "bg-fuchsia-100 dark:bg-fuchsia-500/20", icon: "text-fuchsia-600 dark:text-fuchsia-400" },
};

// Title icons for Rank Progress (shared with lib/titles)
const TITLE_LUCIDE_ICONS: Record<string, React.ReactNode> = {
  Baby: <Baby size={28} strokeWidth={2} />,
  Sprout: <Sprout size={28} strokeWidth={2} />,
  Search: <Search size={28} strokeWidth={2} />,
  GraduationCap: <GraduationCap size={28} strokeWidth={2} />,
  Book: <Book size={28} strokeWidth={2} />,
  Languages: <Languages size={28} strokeWidth={2} />,
  Award: <Award size={28} strokeWidth={2} />,
  Star: <Star size={28} strokeWidth={2} />,
  Crown: <Crown size={28} strokeWidth={2} />,
  Ghost: <Ghost size={28} strokeWidth={2} />,
  Zap: <Zap size={28} strokeWidth={2} />,
  Sparkles: <Sparkles size={28} strokeWidth={2} />,
  Brain: <Brain size={28} strokeWidth={2} />,
  BookOpen: <BookOpen size={28} strokeWidth={2} />,
  Eye: <Eye size={28} strokeWidth={2} />,
  Rocket: <Rocket size={28} strokeWidth={2} />,
};

// Shop categories and items (titles removed - now automatic progression)
type ShopCategory = "boosters" | "themes" | "card_skins";

type ShopLucideIcon =
  | "Zap"
  | "Rocket"
  | "Crown";

interface ShopItemDef {
  id: string;
  category: ShopCategory;
  price: number;
  owned: boolean;
  equipped: boolean;
  emoji: string;
  lucideIcon?: ShopLucideIcon;
}

const SHOP_LUCIDE_ICONS: Record<ShopLucideIcon, React.ReactNode> = {
  Zap: <Zap size={24} strokeWidth={2} />,
  Rocket: <Rocket size={24} strokeWidth={2} />,
  Crown: <Crown size={24} strokeWidth={2} />,
};

const MOCK_SHOP_ITEMS: ShopItemDef[] = [
  { id: "streakFreeze1", category: "boosters", price: 500, owned: false, equipped: false, emoji: "❄️" },
  { id: "streakFreeze3", category: "boosters", price: 1200, owned: false, equipped: false, emoji: "🧊" },
  { id: "crystalDoubler", category: "boosters", price: 1500, owned: false, equipped: false, emoji: "⚡", lucideIcon: "Zap" },
  { id: "streakRestore", category: "boosters", price: 3000, owned: false, equipped: false, emoji: "♻️" },
  { id: "matrixTheme", category: "themes", price: 1000, owned: false, equipped: false, emoji: "🟢" },
  { id: "neonTheme", category: "themes", price: 5000, owned: false, equipped: false, emoji: "✨" },
  { id: "sakuraTheme", category: "themes", price: 15000, owned: false, equipped: false, emoji: "🌸" },
  { id: "cyberpunkTheme", category: "themes", price: 50000, owned: false, equipped: false, emoji: "🤖" },
  { id: "holographicBorder", category: "card_skins", price: 2000, owned: true, equipped: false, emoji: "🃏" },
  { id: "fireBorder", category: "card_skins", price: 5000, owned: false, equipped: false, emoji: "🔥" },
  { id: "pixelBorder", category: "card_skins", price: 10000, owned: false, equipped: false, emoji: "👾" },
  { id: "spaceBorder", category: "card_skins", price: 20000, owned: false, equipped: false, emoji: "🚀", lucideIcon: "Rocket" },
];

const SHOP_CATEGORY_ORDER: ShopCategory[] = ["boosters", "themes", "card_skins"];

/** Build calendar days for the last 30 days in reverse-chronological order (Today first).
 * Active = date is in studyHistory, or today if currentStreak > 0. */
function buildStreakCalendarDays(studyHistory: string[], currentStreak: number) {
  const historySet = new Set(studyHistory);
  const today = new Date().toISOString().slice(0, 10);
  return Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i); // i=0 → Today, i=1 → Yesterday, i=29 → 29 days ago
    const yyyymmdd = d.toISOString().slice(0, 10);
    const active = historySet.has(yyyymmdd) || (yyyymmdd === today && currentStreak > 0);
    return { date: d, active };
  });
}

const TIER_IDS: TierId[] = ["bronze", "silver", "gold", "platinum", "diamond"];

const LEVEL_STYLES: Record<TierId, { gradient: string; glow: string }> = {
  bronze: {
    gradient: "linear-gradient(135deg, #cd7f32 0%, #8b4513 100%)",
    glow: "0 0 20px rgba(205, 127, 50, 0.4)",
  },
  silver: {
    gradient: "linear-gradient(135deg, #c0c0c0 0%, #808080 100%)",
    glow: "0 0 20px rgba(192, 192, 192, 0.5)",
  },
  gold: {
    gradient: "linear-gradient(135deg, #ffd700 0%, #ff8c00 100%)",
    glow: "0 0 24px rgba(255, 215, 0, 0.6)",
  },
  platinum: {
    gradient: "linear-gradient(135deg, #e5e4e2 0%, #a0a0a0 100%)",
    glow: "0 0 24px rgba(229, 228, 226, 0.6)",
  },
  diamond: {
    gradient: "linear-gradient(135deg, #b9f2ff 0%, #00d4ff 50%, #0099cc 100%)",
    glow: "0 0 28px rgba(0, 212, 255, 0.5)",
  },
};

function getBadgeTierInfo(thresholds: [number, number, number, number, number], currentProgress: number): {
  tierIndex: number;
  tierId: TierId;
  progressPercent: number;
  nextTierThreshold: number | null;
  isMaxTier: boolean;
} {
  let tierIndex = 0;
  for (let i = 0; i < thresholds.length; i++) {
    if (currentProgress >= thresholds[i]) tierIndex = i;
  }
  const isMaxTier = tierIndex >= thresholds.length - 1;
  const nextTierThreshold = isMaxTier ? null : thresholds[tierIndex + 1];
  const progressPercent = isMaxTier
    ? 100
    : nextTierThreshold
    ? Math.min(100, (currentProgress / nextTierThreshold) * 100)
    : 0;
  return {
    tierIndex,
    tierId: TIER_IDS[tierIndex],
    progressPercent,
    nextTierThreshold,
    isMaxTier,
  };
}

export default function AchievementsDashboard({
  asPage = false,
  open = true,
  onClose,
  initialTab,
}: AchievementsDashboardProps) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isExpanded, setIsExpanded] = useState(false);
  const { crystals, xp, currentStreak: streak, studyHistory, stats, spendCrystals, ownedItems, addOwnedItem, equipItem, unequipItem, equippedItems, equippedTheme, equippedCardSkin } = useGamification();

  const urlTab = searchParams.get("tab");
  const isLeaderboard = urlTab === "leaderboard";
  const progressTab = (["badges", "streaks", "titles", "shop"] as const).includes(urlTab as TabId) ? (urlTab as TabId) : "titles";

  const topLevelTab: "progress" | "leaderboard" = isLeaderboard ? "leaderboard" : "progress";
  const activeTab = topLevelTab === "progress" ? progressTab : "titles";

  const setTopLevelTab = useCallback(
    (next: "progress" | "leaderboard") => {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          if (next === "leaderboard") {
            nextParams.set("tab", "leaderboard");
            if (!nextParams.has("sub")) nextParams.set("sub", "students");
          } else {
            nextParams.set("tab", "titles");
            nextParams.delete("sub");
          }
          return nextParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setActiveTab = useCallback(
    (next: TabId) => {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.set("tab", next);
          nextParams.delete("sub");
          return nextParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // When modal opens with initialTab (no URL), sync to that tab (page mode uses URL)
  useEffect(() => {
    if (!asPage && open && initialTab) {
      setActiveTab(initialTab);
    }
  }, [asPage, open, initialTab, setActiveTab]);

  // Reset expanded state when switching between badges/titles tabs
  useEffect(() => {
    setIsExpanded(false);
  }, [activeTab]);
  const balance = crystals;
  const ownedItemIds = new Set(ownedItems);

  const getBadgeProgress = useCallback((statKey: StatKey): number => {
    if (statKey === "currentStreak") return streak;
    return stats[statKey] ?? 0;
  }, [streak, stats]);

  const handleBuy = useCallback(
    (item: ShopItemDef) => {
      if (!ownedItemIds.has(item.id) && item.price > 0 && balance >= item.price) {
        spendCrystals(item.price);
        addOwnedItem(item.id);
        playPurchaseSound();
        toast.success(t("achievements.shop.purchaseSuccess"), {
          duration: 2200,
          style: { background: "hsl(var(--card-bg))", border: "1px solid hsl(var(--border))" },
        });
      }
    },
    [balance, spendCrystals, addOwnedItem, ownedItems, t]
  );

  const tabs: { id: TabId; labelKey: string; icon: React.ReactNode; iconClass: string }[] = [
    { id: "titles", labelKey: "achievements.tabs.titles", icon: <Crown size={18} />, iconClass: "text-yellow-600 dark:text-yellow-400" },
    { id: "badges", labelKey: "achievements.tabs.badges", icon: <Award size={18} />, iconClass: "text-amber-600 dark:text-amber-400" },
    { id: "streaks", labelKey: "achievements.tabs.streaks", icon: <Flame size={18} />, iconClass: "text-orange-600 dark:text-orange-400" },
    { id: "shop", labelKey: "achievements.tabs.shop", icon: <Store size={18} />, iconClass: "text-emerald-600 dark:text-emerald-400" },
  ];

  const rankProgress = getRankProgress(xp);

  if (!asPage && !open) return null;

  const content = (
    <>
        {/* Close button (modal only) */}
        {!asPage && onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-white/10 dark:hover:text-gray-300 rounded-full transition-colors z-10"
          aria-label={t("common.close")}
        >
          <X size={20} />
        </button>
        )}

        {/* Unified wrapper: single consistent horizontal padding */}
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden px-6 sm:px-8">
          {/* Header row — centered on mobile, left-aligned on desktop */}
          <div className="flex items-center justify-center md:justify-start w-full pt-6 shrink-0">
            <div className="text-lg md:text-3xl font-bold flex items-center justify-center md:justify-start gap-3 text-slate-900 dark:text-white achievements-modal-title text-center md:text-left" style={{ fontFamily: "DM Sans, sans-serif" }}>
              <span
                className="flex items-center justify-center w-10 h-10 rounded-xl shrink-0"
                style={{
                  background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent2)))",
                }}
              >
                <Gamepad2 size={22} className="text-white" />
              </span>
              {t("achievements.title")}
            </div>
          </div>

          {/* Top-level tab: My Progress | Leaderboard — full width on mobile for easy tap */}
          <div
            className="mt-6 shrink-0 grid grid-cols-2 w-full md:flex md:gap-0 md:w-fit rounded-lg p-1"
            style={{
              background: "hsl(var(--bg3))",
            }}
          >
            <button
              type="button"
              onClick={() => setTopLevelTab("progress")}
              className={`flex items-center justify-center gap-2 h-10 px-5 rounded-md font-medium text-sm transition-colors ${
                topLevelTab === "progress"
                  ? "bg-[hsl(var(--primary))] !text-white"
                  : "bg-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10"
              }`}
            >
              <TrendingUp size={18} className={topLevelTab === "progress" ? "shrink-0 !text-white" : "shrink-0"} />
              <span className={topLevelTab === "progress" ? "!text-white" : undefined}>{t("achievements.myProgress")}</span>
            </button>
            <button
              type="button"
              onClick={() => setTopLevelTab("leaderboard")}
              className={`flex items-center justify-center gap-2 h-10 px-5 rounded-md font-medium text-sm transition-colors ${
                topLevelTab === "leaderboard"
                  ? "bg-[hsl(var(--primary))] !text-white"
                  : "bg-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10"
              }`}
            >
              <Trophy size={18} className={topLevelTab === "leaderboard" ? "shrink-0 !text-white" : "shrink-0"} />
              <span className={topLevelTab === "leaderboard" ? "!text-white" : undefined}>{t("achievements.leaderboardTab")}</span>
            </button>
          </div>

          {topLevelTab === "leaderboard" ? (
            <div className="flex-1 min-h-0 overflow-auto mt-6">
              <Leaderboard />
            </div>
          ) : (
          <>
          {/* Progress section — spans full width of wrapper; compact padding on mobile */}
          <div
            className="mt-8 shrink-0 rounded-2xl p-3 sm:p-5 achievements-progress-section"
            style={{
              background: "linear-gradient(135deg, rgba(99,102,241,.08), rgba(168,85,247,.08))",
              border: "1px solid rgba(139,92,246,.25)",
            }}
          >
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-[hsl(var(--muted-foreground))] mb-3">
              {t("achievements.rankProgress.label")}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 shrink-0">
                <div
                  className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center ${TITLE_ICON_STYLES[rankProgress.current.lucideIcon]?.bg ?? "bg-purple-100 dark:bg-purple-500/20"}`}
                  style={{ boxShadow: "0 0 20px rgba(139,92,246,.2)" }}
                >
                  {React.cloneElement(TITLE_LUCIDE_ICONS[rankProgress.current.lucideIcon] as React.ReactElement<{ className?: string }>, {
                    className: TITLE_ICON_STYLES[rankProgress.current.lucideIcon]?.icon ?? "text-purple-600 dark:text-purple-400",
                  })}
                </div>
                <div>
                  <div className="font-bold text-lg text-slate-900 dark:text-[hsl(var(--foreground))]">
                    {t(`achievements.shop.items.${rankProgress.current.id}`)}
                  </div>
                  <div className="text-xs text-slate-600 dark:text-[hsl(var(--muted-foreground))]">
                    {rankProgress.isMaxTier
                      ? t("achievements.rankProgress.maxReached")
                      : t("achievements.rankProgress.toNext", {
                          current: rankProgress.crystalsInSegment.toLocaleString(),
                          target: rankProgress.crystalsNeededForNext.toLocaleString(),
                          nextTitle: rankProgress.next ? t(`achievements.shop.items.${rankProgress.next.id}`) : "",
                          percent: Math.round(rankProgress.progressPercent),
                        })}
                  </div>
                </div>
              </div>
              <div className="flex-1 min-w-0 flex items-center gap-3">
                <div
                  className="achievements-rank-progress-container flex-1 h-5 sm:h-6 rounded-full overflow-hidden relative"
                  style={{
                    background: "hsl(var(--bg3))",
                    boxShadow: "inset 0 1px 2px rgba(0,0,0,.1)",
                  }}
                >
                  <div
                    className="achievements-rank-progress-bar h-full rounded-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-700 ease-out"
                    style={{
                      width: `${rankProgress.progressPercent}%`,
                      boxShadow: "0 0 12px rgba(139,92,246,.5)",
                    }}
                  />
                </div>
                {rankProgress.next && (
                  <span
                    className="text-sm font-medium text-slate-600 dark:text-[hsl(var(--muted-foreground))] shrink-0 max-w-[120px] truncate"
                    title={t(`achievements.shop.items.${rankProgress.next.id}`)}
                  >
                    → {t(`achievements.shop.items.${rankProgress.next.id}`)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* TabsList — 2x2 grid on mobile, horizontal row on desktop */}
          <div className="grid grid-cols-2 gap-2 w-full mt-8 shrink-0 px-0 md:flex md:flex-row md:flex-wrap">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center justify-center gap-1.5 md:gap-2 h-9 md:h-10 px-3 md:px-4 rounded-full md:rounded-lg font-medium text-xs md:text-sm transition-colors w-full md:w-auto md:shrink-0 ${
                    isActive ? "!text-white" : ""
                  }`}
                  style={{
                    background: isActive ? "hsl(var(--primary))" : "hsl(var(--bg3))",
                    color: isActive ? "white" : "hsl(var(--muted-foreground))",
                  }}
                >
                  {React.cloneElement(tab.icon as React.ReactElement<{ className?: string }>, {
                    className: isActive ? "!text-white shrink-0" : `${tab.iconClass} shrink-0`,
                  })}
                  <span className={isActive ? "!text-white" : undefined}>{t(tab.labelKey)}</span>
                </button>
              );
            })}
          </div>

          {/* Divider */}
          <div className="mt-8 border-b border-[hsl(var(--border))] shrink-0" />

          {/* TabsContent — extra bottom padding on mobile for BottomTabBar clearance */}
          <div className="flex-1 overflow-y-auto overflow-x-clip pt-8 pb-28 md:pb-6 min-h-0 m-0 pl-4 pr-4 border-none outline-none min-w-0 w-full box-border">
            {activeTab === "badges" && (
              <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 pb-4 w-full box-border">
                {BADGE_DEFINITIONS.map((badge, index) => {
                  const currentProgress = getBadgeProgress(badge.statKey);
                  const tierInfo = getBadgeTierInfo(badge.thresholds, currentProgress);
                  const style = LEVEL_STYLES[tierInfo.tierId];
                  const firstThreshold = badge.thresholds[0] ?? 0;
                  const isUnlocked = currentProgress >= firstThreshold;
                  const hasProgress = currentProgress > 0;
                  return (
                    <div
                      key={badge.id}
                      className={`relative rounded-2xl p-3 sm:p-5 transition-all duration-200 hover:scale-[1.02] hover:shadow-lg flex flex-col h-full ${index >= 2 && !isExpanded ? "hidden md:block" : "block"}`}
                      style={{
                        background: hasProgress
                          ? `linear-gradient(145deg, ${style.gradient}22, transparent 60%)`
                          : "hsl(var(--bg3))",
                        border: `2px solid ${hasProgress ? "transparent" : "hsl(var(--border))"}`,
                        boxShadow: hasProgress ? style.glow : "none",
                      }}
                    >
                      {hasProgress && (
                        <div
                          className="absolute inset-0 rounded-2xl opacity-30 pointer-events-none"
                          style={{ background: style.gradient, filter: "blur(20px)" }}
                        />
                      )}
                      <div className="relative flex flex-col flex-1 min-h-0">
                        <div className="flex items-start justify-between gap-1.5 mb-1.5 sm:mb-2">
                          <div
                            className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center shrink-0 ${BADGE_ICON_STYLES[badge.id]?.bg ?? "bg-gray-100 dark:bg-gray-500/20"} ${!isUnlocked ? "grayscale opacity-50" : ""}`}
                            style={{ transition: "filter 0.3s ease, opacity 0.3s ease" }}
                          >
                            {React.cloneElement(BADGE_LUCIDE_ICONS[badge.lucideIcon] as React.ReactElement<{ className?: string }>, {
                              className: BADGE_ICON_STYLES[badge.id]?.icon ?? "text-gray-600 dark:text-gray-400",
                            })}
                          </div>
                          {isUnlocked ? (
                            <Check size={18} className="text-green-600 shrink-0 mt-0.5" strokeWidth={2.5} />
                          ) : (
                            <Lock size={16} className="text-[hsl(var(--muted-foreground))] shrink-0 mt-1" />
                          )}
                        </div>
                        <div className={`font-semibold text-xs sm:text-sm line-clamp-1 ${!isUnlocked ? "text-[hsl(var(--muted-foreground))]" : ""}`}>
                          {t(`achievements.badges.${badge.id}`)}
                        </div>
                        <div className="text-[10px] sm:text-xs text-[hsl(var(--muted-foreground))] mt-0.5 line-clamp-2">
                          {t(`achievements.badges.${badge.id}Desc`)}
                        </div>
                        <div className="mt-auto">
                        <div
                          className={`text-[10px] font-medium mt-2 px-2 py-0.5 rounded-full inline-block ${
                            tierInfo.tierId === "diamond" ? "text-slate-900 drop-shadow-md" : ""
                          }`}
                          style={{
                            background: isUnlocked ? style.gradient : "hsl(var(--bg3))",
                            color: isUnlocked ? (tierInfo.tierId === "diamond" ? undefined : "white") : "hsl(var(--muted-foreground))",
                          }}
                        >
                          {isUnlocked ? t(`achievements.badges.levels.${tierInfo.tierId}`) : t("achievements.badges.locked")}
                        </div>
                        {/* Progress bar */}
                        <div className="mt-3">
                          <div className="flex justify-between text-[10px] text-[hsl(var(--muted-foreground))] mb-1">
                            <span>{currentProgress.toLocaleString()}</span>
                            {tierInfo.nextTierThreshold ? (
                              <span>{tierInfo.nextTierThreshold.toLocaleString()}</span>
                            ) : (
                              <span>{t("achievements.badges.maxTier")}</span>
                            )}
                          </div>
                          <div
                            className="h-1.5 rounded-full overflow-hidden"
                            style={{ background: "hsl(var(--bg3))" }}
                          >
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{
                                width: `${tierInfo.progressPercent}%`,
                                background: isUnlocked ? style.gradient : "hsl(var(--border))",
                              }}
                            />
                          </div>
                        </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => setIsExpanded((e) => !e)}
                className="md:hidden mt-4 w-full py-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-sm font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--border))]/50 transition-colors"
              >
                {isExpanded ? t("common.showLess") : t("common.showMore")}
              </button>
              </>
            )}

            {activeTab === "streaks" && (
              <div className="max-w-2xl w-full">
                <div
                  className="rounded-2xl p-8 mb-8 text-center"
                  style={{
                    background: "linear-gradient(135deg, rgba(255,90,138,.15), rgba(255,90,138,.05))",
                    border: "1px solid rgba(255,90,138,.25)",
                  }}
                >
                  <div className="flex justify-center mb-2">
                    <div className="w-16 h-16 rounded-2xl bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center">
                      <Flame className="w-10 h-10 text-orange-600 dark:text-orange-400" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-[hsl(var(--accent3))]">{streak}</div>
                  <div className="text-sm text-[hsl(var(--muted-foreground))]">
                    {t("achievements.streaks.currentStreak")} · {t("achievements.streaks.days")}
                  </div>
                  <div className="text-sm mt-2 text-[hsl(var(--accent3))] font-medium">
                    {t("achievements.streaks.keepGoing")}
                  </div>
                  <div className="text-xs mt-3 text-[hsl(var(--muted-foreground))] px-4">
                    {t("achievements.streaks.weeklyBonusHint", {
                      count: streak > 0 ? ((7 - (streak % 7)) % 7) || 7 : 7,
                    })}
                  </div>
                </div>
                <div className="text-sm font-medium mb-3">{t("achievements.streaks.last30Days")}</div>
                <div className="grid grid-cols-7 sm:grid-cols-14 gap-2">
                  {buildStreakCalendarDays(studyHistory, streak).map((day, i) => (
                    <div
                      key={i}
                      className="aspect-square rounded-lg flex flex-col items-center justify-center gap-1"
                      style={{
                        background: day.active ? "rgba(255,90,138,.2)" : "hsl(var(--bg3))",
                        border: `1px solid ${day.active ? "rgba(255,90,138,.4)" : "hsl(var(--border))"}`,
                      }}
                    >
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">
                        {day.date.getDate()}
                      </span>
                      {day.active && <Flame size={14} className="text-[hsl(var(--accent3))]" />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "titles" && (
              <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 pb-4 w-full box-border">
                {TITLE_DEFINITIONS.map((title, index) => {
                  const xpThreshold = RANK_XP_THRESHOLDS[index] ?? title.threshold;
                  const isAchieved = xp >= xpThreshold;
                  const isCurrent = title.id === rankProgress.current.id;
                  const isLocked = !isAchieved;
                  return (
                    <div
                      key={title.id}
                      className={`relative rounded-2xl p-3 sm:p-4 transition-all duration-200 flex-shrink-0 ${index >= 2 && !isExpanded ? "hidden md:block" : "block"} ${
                        isCurrent
                          ? "ring-2 ring-blue-500 shadow-lg shadow-blue-500/25"
                          : isLocked
                          ? "opacity-60 grayscale"
                          : ""
                      }`}
                      style={{
                        background: isAchieved
                          ? "linear-gradient(135deg, rgba(99,102,241,.12), rgba(168,85,247,.08))"
                          : "hsl(var(--bg3))",
                        border: isCurrent
                          ? "1px solid rgba(99,102,241,.4)"
                          : `1px solid ${isLocked ? "hsl(var(--border))" : "rgba(139,92,246,.2)"}`,
                      }}
                    >
                      <div className="flex items-start justify-between gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                        <div
                          className={`w-9 h-9 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0 ${TITLE_ICON_STYLES[title.lucideIcon]?.bg ?? "bg-gray-100 dark:bg-gray-500/20"} ${isLocked ? "opacity-60" : ""}`}
                        >
                          {React.cloneElement(TITLE_LUCIDE_ICONS[title.lucideIcon] as React.ReactElement<{ className?: string }>, {
                            className: TITLE_ICON_STYLES[title.lucideIcon]?.icon ?? "text-gray-600 dark:text-gray-400",
                          })}
                        </div>
                        {isAchieved && !isCurrent && (
                          <Check size={18} className="text-green-600 shrink-0 mt-0.5" strokeWidth={2.5} />
                        )}
                        {isLocked && (
                          <Lock size={16} className="text-[hsl(var(--muted-foreground))] shrink-0 mt-1" />
                        )}
                      </div>
                      <div
                        className={`font-semibold text-xs sm:text-sm line-clamp-1 ${
                          isLocked ? "text-[hsl(var(--muted-foreground))]" : "text-[hsl(var(--foreground))]"
                        }`}
                      >
                        {t(`achievements.shop.items.${title.id}`)}
                      </div>
                      <div className="text-[10px] sm:text-xs mt-0.5 sm:mt-1 text-[hsl(var(--muted-foreground))]">
                        {isLocked ? (
                          <span className="font-medium">
                            {xpThreshold.toLocaleString()} XP
                          </span>
                        ) : (
                          <span>#{index + 1}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => setIsExpanded((e) => !e)}
                className="md:hidden mt-4 w-full py-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-sm font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--border))]/50 transition-colors"
              >
                {isExpanded ? t("common.showLess") : t("common.showMore")}
              </button>
              </>
            )}

            {activeTab === "shop" && (
              <div className="w-full">
                <div className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
                  {t("achievements.shop.balance")}:{" "}
                  <span className="font-semibold text-[hsl(var(--accent2))]">
                    {balance.toLocaleString()} 💎
                  </span>
                </div>
                {SHOP_CATEGORY_ORDER.map((category) => {
                  const items = MOCK_SHOP_ITEMS.filter((i) => i.category === category);
                  if (items.length === 0) return null;
                  return (
                    <div key={category} className="mb-8">
                      <h3 className="text-base font-bold text-slate-900 dark:text-[hsl(var(--foreground))] mb-3">
                        {t(`achievements.shop.categories.${category}`)}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 w-full">
                        {items.map((item) => {
                          const isOwned = ownedItemIds.has(item.id);
                          const isEquipped =
                            item.category === "themes"
                              ? equippedTheme === item.id
                              : item.category === "card_skins"
                              ? equippedCardSkin === item.id
                              : equippedItems.includes(item.id);
                          const canBuy = !isOwned && item.price > 0 && balance >= item.price;
                          return (
                            <div
                              key={item.id}
                              className="rounded-2xl p-5 border border-[hsl(var(--border))] transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:border-[hsl(var(--primary))]/30 flex flex-col h-full"
                              style={{ background: "hsl(var(--card-bg))" }}
                            >
                              <div
                                className="w-12 h-12 rounded-xl mb-3 flex items-center justify-center text-2xl text-[hsl(var(--foreground))] shrink-0"
                                style={{
                                  background:
                                    item.category === "themes"
                                      ? "linear-gradient(135deg, #ff00ff, #00ffff)"
                                      : item.category === "card_skins"
                                      ? "linear-gradient(135deg, #667eea, #764ba2)"
                                      : "linear-gradient(135deg, hsl(var(--accent2)), hsl(var(--primary)))",
                                }}
                              >
                                {item.lucideIcon ? SHOP_LUCIDE_ICONS[item.lucideIcon] : item.emoji}
                              </div>
                              <div className="font-semibold text-sm">
                                {t(`achievements.shop.items.${item.id}`)}
                              </div>
                              <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                                {t(`achievements.shop.items.${item.id}Desc`)}
                              </div>
                              <div className="text-sm font-medium mt-2 text-[hsl(var(--accent2))]">
                                {isOwned ? t("achievements.shop.owned") : t("achievements.shop.price", { count: item.price })}
                              </div>
                              <div className="mt-auto flex gap-2 pt-3">
                                {isOwned ? (
                                  <button
                                    onClick={() =>
                                      item.category === "themes"
                                        ? equipItem(item.id, "themes")
                                        : isEquipped
                                          ? unequipItem(item.id)
                                          : equipItem(
                                              item.id,
                                              item.category === "card_skins" ? "card_skins" : undefined
                                            )
                                    }
                                    className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors achievements-shop-btn"
                                    style={{
                                      background: isEquipped ? "hsl(var(--primary))" : "hsl(var(--bg3))",
                                    }}
                                  >
                                    {isEquipped ? `✓ ${t("achievements.shop.equipped")}` : t("achievements.shop.equip")}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleBuy(item)}
                                    disabled={!canBuy}
                                    title={!canBuy && balance < item.price ? t("achievements.shop.insufficientBalance") : undefined}
                                    className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed achievements-shop-btn"
                                    style={{
                                      background: canBuy ? "hsl(var(--primary))" : "hsl(var(--bg3))",
                                    }}
                                  >
                                    {t("achievements.shop.buy")} {item.price > 0 && `· ${item.price.toLocaleString()} 💎`}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          </>
          )}
        </div>
    </>
  );

  if (asPage) {
    return (
      <div className="flex flex-col flex-1 min-h-0 overflow-auto">
        <div
          className="achievements-dashboard bg-white dark:bg-[hsl(var(--card-bg))] w-full max-w-5xl md:max-w-6xl mx-auto flex-1 rounded-3xl shadow-2xl overflow-hidden flex flex-col relative flex-shrink-0 my-6"
          style={{ minHeight: "min(800px, calc(100vh - 120px))" }}
        >
          {content}
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 sm:p-6 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        className="achievements-dashboard bg-white dark:bg-[hsl(var(--card-bg))] w-full max-w-5xl h-[85vh] sm:h-[80vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col relative flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </div>
    </div>
  );
}
